import { useRef, useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import "bootstrap-icons/font/bootstrap-icons.css";

import ProfileCreateLayout from "./ProfileCreateLayout";
import BasicDetailsStep from "./BasicDetailsStep";
import IndustryInfoStep from "./IndustryInfoStep";
import SetupProgressStep from "./SetupProgressStep";
import ProfileCreateFooter from "./ProfileCreateFooter";
import StepProgressBar from "../../step_progress_bar/StepProgressBar";
import Loader from "../../loader/Loader";
import { ProfileSetupProvider } from "../../../context/ProfileSetupContext";
import { useBusinessProfileForm, validateBasicDetailsFields, validateIndustryInfoFields } from "../useBusinessProfileForm";
import postBusinessProfile from "../../../apis/postBusinessProfile";
import { buildBusinessProfilePayload } from "../../../utils/businessProfilePayload";
import { fetchBusinessDetails } from "../../../store/profileSlice";
import { trackProfileCreated } from "../../../analytics/analytics";

const STEP_KEYS = ["basicDetails", "industryInfo", "setupProgress"];
const stepObject = Object.fromEntries(STEP_KEYS.map((key) => [key, true]));

const STEP_HEADINGS = {
  basicDetails: {
    title: "Basic Details",
    subtitle: "Tell us about your business so we can personalize your experience.",
  },
  industryInfo: {
    title: "Industry Info",
    subtitle: "Help us understand your market and location.",
  },
  setupProgress: {
    title: "Setup Progress",
    subtitle: "We're preparing marketing trends for your business.",
  },
};

export default function ProfileCreateWizard({
  embedded = false,
  onComplete = null,
  onEmbeddedFooter = null,
}) {
  const dispatch = useDispatch();
  const form = useBusinessProfileForm({ mode: "create", inputSize: embedded ? "sm" : "md" });
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState("next");
  const [submitting, setSubmitting] = useState(false);
  const [stepError, setStepError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [industrySuggesting, setIndustrySuggesting] = useState(false);
  const submitInFlightRef = useRef(false);
  const profileCreateDoneRef = useRef(false);

  const stepKey = STEP_KEYS[currentStep];
  const isSetupStep = stepKey === "setupProgress";
  const heading = STEP_HEADINGS[stepKey];
  const isBusy = submitting || industrySuggesting;

  const validateBasicStep = () => {
    const errors = validateBasicDetailsFields(form.formData);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateIndustryStep = () => {
    const errors = validateIndustryInfoFields(form.formData);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePrev = useCallback(() => {
    if (currentStep <= 0 || isSetupStep || submitting || industrySuggesting || submitInFlightRef.current) return;
    setDirection("prev");
    setStepError(null);
    setFieldErrors({});
    setCurrentStep((prev) => prev - 1);
  }, [currentStep, isSetupStep, submitting, industrySuggesting]);

  const handleNext = useCallback(async () => {
    if (isSetupStep || submitting || industrySuggesting || submitInFlightRef.current) return;

    setDirection("next");
    setStepError(null);

    if (currentStep === 0) {
      if (!validateBasicStep()) return;
      setCurrentStep((prev) => prev + 1);
      return;
    }

    if (currentStep === 1) {
      if (!validateIndustryStep()) return;

      if (profileCreateDoneRef.current) {
        setCurrentStep((prev) => prev + 1);
        return;
      }

      submitInFlightRef.current = true;
      setSubmitting(true);

      try {
        const payload = buildBusinessProfilePayload(form.formData);
        await postBusinessProfile(payload);
        profileCreateDoneRef.current = true;
        setCurrentStep((prev) => prev + 1);
        toast("Business profile created!");
        trackProfileCreated({
          industry: payload.industry,
          category: payload.category,
        });
        await dispatch(fetchBusinessDetails()).unwrap();
      } catch (err) {
        console.error(err);
        toast("Oops, something went wrong!");
        setStepError("Could not save your profile. Please try again.");
      } finally {
        submitInFlightRef.current = false;
        setSubmitting(false);
      }
      return;
    }

    setCurrentStep((prev) => prev + 1);
  }, [currentStep, isSetupStep, submitting, industrySuggesting, form.formData, dispatch]);

  useEffect(() => {
    if (!embedded || !onEmbeddedFooter) return undefined;

    if (isSetupStep) {
      onEmbeddedFooter(null);
      return () => onEmbeddedFooter(null);
    }

    onEmbeddedFooter({
      onPrev: handlePrev,
      onNext: handleNext,
      canGoBack: currentStep > 0,
      isBusy,
      submitting,
      industrySuggesting,
      isLastFormStep: currentStep === 1,
    });

    return () => onEmbeddedFooter(null);
  }, [
    embedded,
    onEmbeddedFooter,
    isSetupStep,
    currentStep,
    isBusy,
    submitting,
    industrySuggesting,
    handlePrev,
    handleNext,
  ]);

  const renderStep = () => {
    switch (stepKey) {
      case "basicDetails":
        return <BasicDetailsStep form={form} fieldErrors={fieldErrors} compact={embedded} />;
      case "industryInfo":
        return (
          <IndustryInfoStep
            form={form}
            fieldErrors={fieldErrors}
            onSuggestingChange={setIndustrySuggesting}
            compact={embedded}
          />
        );
      case "setupProgress":
        return <SetupProgressStep embedded={embedded} onComplete={onComplete} />;
      default:
        return null;
    }
  };

  const wizardContent = (
    <>
      <Loader isLoading={submitting} />
      <div className={embedded ? "w-full" : "mx-auto w-full max-w-5xl"}>
        {!isSetupStep && !embedded ? (
          <div className="mb-6 text-center md:mb-8">
            <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
              Create Your Profile
            </h1>
            <p className="mb-0 text-sm text-slate-600 md:text-base">
              Set up your business profile in a few quick steps.
            </p>
          </div>
        ) : null}

        <div
          className={` ${
            embedded ? "border-0 p-0 shadow-none" : "p-4 md:p-6"
          }`}
        >
          {!isSetupStep ? (
            <>
              <div
                className={
                  embedded
                    ? "mb-3 border-b border-slate-200/80 pb-2"
                    : "sticky top-0 z-10 -mx-4 border-b border-slate-200/80 bg-white/95 px-4 py-2 backdrop-blur-sm md:-mx-6 md:px-6"
                }
              >
                <StepProgressBar currentStep={currentStep} steps={stepObject} iconOnly={embedded} />
              </div>

              <div className={embedded ? "mb-2 mt-2" : "mt-4 mb-2 md:mt-6"}>
                <h2
                  className={
                    embedded
                      ? "mb-0.5 text-base font-semibold text-slate-900"
                      : "mb-1 text-lg font-semibold text-slate-900 md:text-xl"
                  }
                >
                  {heading.title}
                </h2>
                <p className={embedded ? "mb-0 text-xs text-slate-600" : "mb-0 text-sm text-slate-600"}>
                  {heading.subtitle}
                </p>
              </div>
            </>
          ) : null}

          {stepError ? (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {stepError}
            </div>
          ) : null}

          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ x: direction === "next" ? 80 : -80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: direction === "next" ? -80 : 80, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={embedded ? "pt-1" : "pt-2 md:pt-4"}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        {!isSetupStep && !embedded ? (
          <div className="fixed inset-x-0 bottom-0 z-20 md:static md:mt-6">
            <div
              className="pointer-events-none h-8 bg-gradient-to-t from-slate-50 to-transparent md:hidden"
              aria-hidden
            />
            <div className="border-t border-slate-200/80 bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur-sm md:border-0 md:bg-transparent md:px-0 md:pb-0 md:pt-0">
              <div className="mx-auto flex max-w-5xl items-center justify-between py-2 md:px-1 md:py-0">
                <button
                  type="button"
                  className="btn btn-dark-app px-3 py-2 text-sm md:px-5 md:py-2.5"
                  onClick={handlePrev}
                  disabled={currentStep === 0 || isBusy}
                >
                  <i className="bi bi-arrow-left me-2" aria-hidden />
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-dark-app px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 md:px-5 md:py-2.5"
                  onClick={handleNext}
                  disabled={isBusy}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                      Saving…
                    </>
                  ) : industrySuggesting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                      Suggesting…
                    </>
                  ) : currentStep === 1 ? (
                    <>
                      Finish
                      <i className="bi bi-arrow-right ms-2" aria-hidden />
                    </>
                  ) : (
                    <>
                      Next
                      <i className="bi bi-arrow-right ms-2" aria-hidden />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {!embedded ? (
          <ProfileCreateFooter
            className={`mt-8 ${!isSetupStep ? "pb-4 md:mt-10 md:pb-0" : "mt-10 pb-8"}`}
          />
        ) : null}
      </div>
    </>
  );

  if (embedded) {
    return <ProfileSetupProvider>{wizardContent}</ProfileSetupProvider>;
  }

  return (
    <ProfileCreateLayout allowExistingProfile={isSetupStep}>
      {wizardContent}
    </ProfileCreateLayout>
  );
}
